import "../../styles/index.css";
import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import BASE_URL from "../../components/config/appconfig";
import axios from "axios";
import usePageMeta from "../../utils/usePageMeta";

const Verification = () => {
  usePageMeta(
    "Verification",
    "Begin your Gospel and Church Health Framework assessment.",
  );
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);

  const handleBeginAssessment = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));

      if (!userData || !userData._id) {
        console.error("User data not found in localStorage");
        return;
      }

      const response = await axios.post(`${BASE_URL}/api/assessments`, {
        people_id: userData._id,
        status: "in_progress",
      });
      const assessmentId = response.data.data[0]._id;
      navigate("/assessment", {
        state: { assessmentId },
        replace: true,
      });
    } catch (error) {
      console.error("Error creating assessment:", error);
    }
  };

  return (
    <Box className="verification-container">
      <Box sx={{ mt: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" className="verification-title">
          Welcome to the Gospel and Church Health Framework.
        </Typography>
        <Typography variant="body1" className="verification-subtitle">
          You are now invited to begin. This diagnostic is structured into three
          distinct pillars to provide a holistic view of your church's health
          and mission.
        </Typography>
      </Box>

      <Box className="verification-content">
        <Box sx={{ width: "100%" }}>
          <Typography variant="body1" className="instruction-heading">
            {/* How to use The Gospel and Church Health Framework? */}
            Overview
          </Typography>

          <Typography variant="body1" className="instruction-content">
            We encourage churches to sit with their leadership teams every six
            months, prayerfully reflecting on each area of this framework.
          </Typography>
          <Typography variant="body1" className="instruction-content">
            Leaders should ask:
          </Typography>
          <Typography variant="body1" className="instruction-content-nomargin">
            "Where are we flourishing in the Gospel, and where do we need more
            renewal? What could we do differently to help our members grow in
            Christ and reach our city more effectively?"
          </Typography>

          <Typography variant="body1" className="instruction-content">
            This is meant to be a shared spiritual exercise, not an
            administrative audit — a space to align hearts and minds around the
            gift of grace and God's vision for the church. So this framework
            does not just ask:
          </Typography>
          <Typography variant="body1" className="instruction-content-bold">
            "How are we doing as a church?"
          </Typography>
          <Typography variant="body1" className="instruction-content">
            but,
          </Typography>
          <Typography variant="body1" className="instruction-content-bold">
            "Is the Gospel shaping everything?"
          </Typography>

          <Typography variant="body1" className="instruction-content">
            As our hearts get rooted deeper in the Gospel, we feel lesser pride
            in performance, and lesser shame in failure. This frees us to answer
            church health questions honestly – acknowledging things as they are,
            rather than mistaking the ideal for the actual. Only the Gospel can
            liberate us to answer these questions truthfully.
          </Typography>
          <Typography variant="body1" className="instruction-content">
            We are often tempted to measure the church's health and our worth by
            attendance, influence, or program success. When that happens,
            ministry becomes performance, and the heart grows weary. Only the
            Gospel can free us from this burden.
          </Typography>
          <Typography
            variant="body1"
            className="instruction-heading"
            sx={{ mt: 3 }}
          >
            How to use The Gospel and Church Health Framework
          </Typography>
          <Typography variant="body1" className="instruction-content">
            You will see that there are twin tracks that gently guide you
            through this healthy church framework.
          </Typography>
          <Typography
            variant="body1"
            className="instruction-content"
            sx={{
              ml: 5,
              display: "list-item",
              listStyleType: "disc",
              listStylePosition: "outside",
            }}
          >
            In the first track you will be encouraged to reflect on the true
            health of the church that God has called you to lead.
          </Typography>
          <Typography
            variant="body1"
            className="instruction-content"
            sx={{
              ml: 5,
              display: "list-item",
              listStyleType: "disc",
              listStylePosition: "outside",
            }}
          >
            In the second track you will be guided to look into your own heart
            as a leader.
          </Typography>
          <Typography variant="body1" className="instruction-content">
            We need the second track too because ministry can and does so easily
            become an idol. This track invites leaders to pause and let the
            Gospel cut deep. It invites you to reflect:
          </Typography>
          <Typography variant="body1" className="instruction-content-nomargin">
            {" "}
            "Am I resting in Christ's grace, or am I striving to prove my value?
            Am I leading from sonship or from fear or self-reliance?"
          </Typography>
          <Typography variant="body1" className="instruction-content">
            True fruitfulness flows from a Gospel-shaped heart. When leaders
            embrace their identity in Christ, they lead with humility, freedom,
            and joy — modeling the Gospel for the whole church and together. And
            healthy churches model the Gospel to the city.
          </Typography>
          <Typography variant="body1" className="instruction-content">
            We encourage leaders to set aside at least 90 minutes of
            uninterrupted time to journey through these 30 questions for
            reflection and discussion across 10 sections. At the end of it, you
            will receive a summary of your reflections and responses.
          </Typography>
          <Typography variant="body1" className="instruction-content">
            As you walk through the Gospel and Church Health Framework every six
            months, you will be able to see and celebrate how the Gospel is
            bearing fruit in your heart and in the church, as it is indeed
            bearing fruit all over your city and the world.
          </Typography>
          <Typography
            variant="body1"
            className="instruction-content"
            sx={{ fontStyle: "italic" }}
          >
            Note: The idea for The Gospel &amp; Healthy Church Framework was
            sparked by Avant Ministries' Healthy Church.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          ml: 1,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              sx={{
                color: "#263842",
                "&.Mui-checked": { color: "#263842" },
              }}
            />
          }
          label={
            <Typography
              sx={{ fontSize: { xs: "12px", sm: "14px" }, color: "#263842" }}
            >
              I have read the provided guidelines and am ready to proceed
            </Typography>
          }
        />
      </Box>

      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          mt: { xs: 2, sm: 3 },
          mb: { xs: 3, sm: 5 },
        }}
      >
        <Button
          variant="contained"
          disabled={!agreed}
          onClick={handleBeginAssessment}
          sx={{
            bgcolor: "#ed1c2e",
            color: "#fff",
            fontSize: "15px",
            fontWeight: 600,
            px: 4.5,
            py: 1.5,
            borderRadius: "8px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#ed1c2e", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "#f5a0a7", color: "#fff" },
          }}
        >
          Begin
        </Button>
      </Box>
    </Box>
  );
};

export default Verification;
